import React, { FC, Fragment, ReactNode } from 'react';
import { Box, Card, CardContent, Grid, Skeleton, Typography } from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ViewInArOutlinedIcon from '@mui/icons-material/ViewInArOutlined';
import PrecisionManufacturingOutlinedIcon from '@mui/icons-material/PrecisionManufacturingOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import PageTitle from 'components/shared/page-title/page-title.component';
import { organizationUsageTemplate } from 'apollo/templates/usage';
import { OrganizationUsage, STORAGE_BYTES_METRIC, UsageRow } from 'models/usage';
import { formatDate, formatNumber } from 'utils/format';

const BYTES_PER_GIGABYTE = 1024 ** 3;
// MUI gives the last CardContent 24 px bottom padding against 16 px elsewhere; even it out.
const EVEN_CARD_PADDING = { '&:last-child': { paddingBottom: '16px' } };

// Display order, translation key and icon per storage dimension (bucket kind).
const STORAGE_DIMENSIONS: { dimension: string; labelKey: string; icon: ReactNode }[] = [
    { dimension: 'order-files', labelKey: 'lab-settings:USAGE_TYPE_ORDER_FILES', icon: <DescriptionOutlinedIcon /> },
    { dimension: 'scanner-cases', labelKey: 'lab-settings:USAGE_TYPE_SCANNER_CASES', icon: <ViewInArOutlinedIcon /> },
    { dimension: 'milling', labelKey: 'lab-settings:USAGE_TYPE_MILLING', icon: <PrecisionManufacturingOutlinedIcon /> },
    { dimension: 'billing', labelKey: 'lab-settings:USAGE_TYPE_BILLING', icon: <ReceiptLongOutlinedIcon /> },
    { dimension: 'transport', labelKey: 'lab-settings:USAGE_TYPE_TRANSPORT', icon: <LocalShippingOutlinedIcon /> },
    { dimension: 'settings', labelKey: 'lab-settings:USAGE_TYPE_SETTINGS', icon: <SettingsOutlinedIcon /> },
    // DD-3687: historical files get their own line, per the PO.
    {
        dimension: 'historical',
        labelKey: 'lab-settings:USAGE_TYPE_HISTORICAL',
        icon: <Inventory2OutlinedIcon />,
    },
];

const formatGigabytes = (valueInBytes: number): string =>
    `${formatNumber(valueInBytes / BYTES_PER_GIGABYTE, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} GB`;

const UsageCardSkeletons: FC = () => (
    <Grid container spacing={2}>
        <Grid item xs={12}>
            <Skeleton variant="rounded" height={96} />
        </Grid>
        {STORAGE_DIMENSIONS.map(({ dimension }) => (
            <Grid item xs={12} md={6} key={dimension}>
                <Skeleton variant="rounded" height={96} />
            </Grid>
        ))}
    </Grid>
);

type UsageCardRowProps = { icon: ReactNode; label: string; note?: string; value: string; isTotal?: boolean };

/** DD-3674: icon and label (with an optional note under it) on the left, the size on the right. */
const UsageCardRow: FC<UsageCardRowProps> = ({ icon, label, note, value, isTotal }) => (
    <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
        <Box display="flex" alignItems="center" gap={1.5} minWidth={0}>
            <Box color="text.secondary" display="flex" sx={{ '& svg': { fontSize: isTotal ? 40 : 32 } }}>
                {icon}
            </Box>
            <Box minWidth={0}>
                <Typography variant={isTotal ? 'h3' : 'h4'} component="p">
                    {label}
                </Typography>
                {note ? (
                    <Typography variant="body2" color="text.secondary" noWrap>
                        {note}
                    </Typography>
                ) : undefined}
            </Box>
        </Box>
        <Typography variant={isTotal ? 'h1' : 'h3'} component="p" sx={{ whiteSpace: 'nowrap' }}>
            {value}
        </Typography>
    </Box>
);

type UsageCardProps = { icon: ReactNode; label: string; valueInBytes: number };

/** One data type and its size. */
const UsageCard: FC<UsageCardProps> = ({ icon, label, valueInBytes }) => (
    <Card variant="outlined" sx={{ height: '100%' }}>
        <CardContent sx={EVEN_CARD_PADDING}>
            <UsageCardRow icon={icon} label={label} value={formatGigabytes(valueInBytes)} />
        </CardContent>
    </Card>
);

const UsageOverview: FC = () => {
    const { t: translate } = useTranslation(['lab-settings', 'common']);
    const { showBoundary } = useDenthubErrorBoundary();

    const { data, loading } = useQuery<{ organizationUsage: OrganizationUsage }>(organizationUsageTemplate, {
        variables: { metric: STORAGE_BYTES_METRIC },
        fetchPolicy: 'cache-and-network',
        onError(error) {
            showBoundary(error);
        },
    });
    const organizationUsage = data?.organizationUsage;

    const header = (
        <Fragment>
            <PageTitle headingStyle="h2" title={translate('lab-settings:USAGE')} />
            <Box mb={2}>{translate('lab-settings:USAGE_DESC')}</Box>
        </Fragment>
    );
    if (loading && !organizationUsage) {
        return (
            <Fragment>
                {header}
                <UsageCardSkeletons />
            </Fragment>
        );
    }
    if (!organizationUsage) {
        return null;
    }

    const usageRowByDimension = new Map<string | null, UsageRow>(
        organizationUsage.rows.map((usageRow) => [usageRow.dimension, usageRow]),
    );

    return (
        <Fragment>
            {header}
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Card variant="outlined" data-test="usage.total">
                        <CardContent sx={EVEN_CARD_PADDING}>
                            <UsageCardRow
                                isTotal
                                icon={<StorageOutlinedIcon />}
                                label={translate('lab-settings:USAGE_TOTAL')}
                                value={formatGigabytes(organizationUsage.totalValue)}
                            />
                        </CardContent>
                    </Card>
                </Grid>
                {STORAGE_DIMENSIONS.map(({ dimension, labelKey, icon }) => (
                    <Grid item xs={12} md={6} key={dimension} data-test={`usage.${dimension}`}>
                        <UsageCard
                            icon={icon}
                            label={translate(labelKey)}
                            valueInBytes={usageRowByDimension.get(dimension)?.value ?? 0}
                        />
                    </Grid>
                ))}
            </Grid>
            <Box mt={2}>
                <Typography variant="body2" color="text.secondary" data-test="usage.last-calculated">
                    {organizationUsage.lastCalculatedAt
                        ? translate('lab-settings:USAGE_LAST_CALCULATED', {
                              calculatedAt: formatDate(organizationUsage.lastCalculatedAt),
                          })
                        : translate('lab-settings:USAGE_NOT_CALCULATED_YET')}
                </Typography>
            </Box>
        </Fragment>
    );
};

export default UsageOverview;
